/**
 * The portfolio's first-load entrance, shared between the parts that play
 * it: PageTransition (decides it, paints the loader backdrop and the
 * counter before the page chunk has even arrived), Portfolio (drives the
 * WebGL scene and reports real progress), and Nav / Footer (hold back
 * their chrome until the slides are settling).
 *
 *   none      no entrance: any other page, a route change, reduced motion.
 *   boot      first load of the carousel: waiting for the page chunk.
 *   loading   the carousel is forming its ring while the strip loads.
 *   expanding the ring has started unrolling: the loader UI fades out.
 *   settling  the slides are still settling: nav, titles, footer come in.
 *   done      the carousel is itself; everything behaves as always.
 *
 * Writes from the render loop go through `getDefaultStore()` (the app has
 * no Provider, so hooks read the same store).
 */
import { atom, getDefaultStore } from "jotai";
import { ROUTES } from "../routes";

export type EntrancePhase = "none" | "boot" | "loading" | "expanding" | "settling" | "done";

/** The loader UI (backdrop, counter, caption, skip) is on screen. */
export const isLoaderPhase = (phase: EntrancePhase) =>
    phase === "boot" || phase === "loading" || phase === "expanding";
/** Nav links, slide titles and footer are allowed in. */
export const isChromePhase = (phase: EntrancePhase) =>
    phase === "none" || phase === "settling" || phase === "done";

/** Decided at module load, not in an effect, so the shell's very first
 *  paint already holds the chrome back: an effect runs after that paint,
 *  and one frame of nav links on the loader backdrop is a visible flicker.
 *  Mirrors PageTransition's first-load branch, which still owns the side
 *  effects (root colour, curtains) and re-sets "boot" harmlessly. */
const initialPhase = (): EntrancePhase =>
    typeof window !== "undefined"
    && window.location.pathname === ROUTES.index
    && !window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "boot"
        : "none";

export const entrancePhaseAtom = atom<EntrancePhase>(initialPhase());

/** Real loading progress, 0 → 1 (see animationConfig.portfolio.entrance.progress). */
export const entranceProgressAtom = atom(0);

/** The counter has landed on 100: the unroll may begin (with the scene
 *  ready). Set by EntranceLoader, read by the carousel's render loop. */
export const entranceCountDoneAtom = atom(false);

/** When the ring will be complete (`endMs`, performance.now() ms), as
 *  the carousel last reckoned it at `startMs`: the counter paces itself
 *  to land on 100 then, when loading runs ahead of it. Published once
 *  the ring starts dealing, and again should the reckoning drift. */
export const entranceFormationAtom = atom<{ startMs: number; endMs: number } | null>(null);

/** Set by the skip button / Escape / Tab; the controller jumps to the end. */
export const entranceSkipAtom = atom(false);

export const entranceStore = getDefaultStore();

/** "boot" starts an entrance over (the book's door back to the carousel
 *  plays it again), so what the last one left is cleared with it: the
 *  counter would otherwise mount on a progress of 1 and show 100
 *  throughout. */
export const setEntrancePhase = (phase: EntrancePhase) => {
    if (phase === "boot") {
        entranceStore.set(entranceProgressAtom, 0);
        entranceStore.set(entranceCountDoneAtom, false);
        entranceStore.set(entranceFormationAtom, null);
        entranceStore.set(entranceSkipAtom, false);
    }
    entranceStore.set(entrancePhaseAtom, phase);
};
export const markEntranceCountDone = () => entranceStore.set(entranceCountDoneAtom, true);
export const setEntranceFormation = (startMs: number, endMs: number) =>
    entranceStore.set(entranceFormationAtom, { startMs, endMs });
export const setEntranceProgress = (p: number) => {
    const clamped = p < 0 ? 0 : p > 1 ? 1 : p;
    if (entranceStore.get(entranceProgressAtom) !== clamped) entranceStore.set(entranceProgressAtom, clamped);
};
export const requestEntranceSkip = () => entranceStore.set(entranceSkipAtom, true);

/** CSS custom property the loader backdrop reads its opacity from, so the
 *  render loop can crossfade it without a React update per frame. */
export const ENTRANCE_BACKDROP_VAR = "--entrance-backdrop";
